import { AxiosResponse } from "axios";

import {
  IArchiveMeasurementRequest,
  IMeasurement,
  IMeasurementsFilters,
  IMeasurementTypeValidation,
  IUpdateMeasurementRequest,
} from "../../@types/admin/AdminMeasurement";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { api } from "../api";

/**
 * Measurements admin page (EN-5158), migrated from the Retool
 * MeasurementList page and its module.
 *
 * This list is server-paginated, unlike the other migrated pages: it spans
 * every patient's measurements, so there is no catalogue-sized set to hold
 * client-side. Retool paged it the same way (`serverPaginated`, page size 50).
 */
export const measurementsService = {
  async getMeasurements(
    filters: IMeasurementsFilters
  ): Promise<AxiosResponse<IPaginatedResponse<IMeasurement[]>>> {
    return api.get<IPaginatedResponse<IMeasurement[]>>("/Retool/Measurements", {
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        // Retool sent every filter on every request, empty string included.
        // Undefined values are dropped by axios, which keeps the URL (and the
        // query key) honest about what is actually being filtered on.
        patientName: filters.patientName || undefined,
        wardName: filters.wardName || undefined,
        hospitalName: filters.hospitalName || undefined,
        measurementType: filters.measurementType || undefined,
        source: filters.source || undefined,
      },
    });
  },

  /**
   * One patient's measurements (EN-5169) — the Patients detail page's
   * Measurements panel.
   *
   * A patient-scoped route, not `getMeasurements({ patientId })`: the page's
   * own `patients:access` grant reaches this one, and it must not also open an
   * unfiltered read of every measurement in the region.
   */
  async getPatientMeasurements(
    patientId: number,
    page: number,
    pageSize: number
  ): Promise<AxiosResponse<IPaginatedResponse<IMeasurement[]>>> {
    return api.get<IPaginatedResponse<IMeasurement[]>>(
      `/Retool/Measurements/Patients/${patientId}`,
      { params: { page, pageSize } }
    );
  },

  /** Measurement types with the min/max the edit form validates against. */
  async getMeasurementTypes(): Promise<
    AxiosResponse<IMeasurementTypeValidation[]>
  > {
    return api.get<IMeasurementTypeValidation[]>("/Retool/Measurements/Types");
  },

  async updateMeasurement(
    measurementId: number,
    request: IUpdateMeasurementRequest
  ): Promise<AxiosResponse<void>> {
    return api.put<void>(`/Retool/Measurements/${measurementId}`, request);
  },

  async archiveMeasurement(
    request: IArchiveMeasurementRequest
  ): Promise<AxiosResponse<void>> {
    return api.post<void>("/Retool/Measurements/Archive", request);
  },
};
